import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, Mail } from 'lucide-react';
import { MOCK_SERVICES } from '../data';
import { imageUrl } from '../lib/images';

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(MOCK_SERVICES.find(s => s.url_slug === slug));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchService() {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const found = data.find(s => s.slug.da === slug || s.url_slug === slug);
          if (found) setService(found);
        }
      } catch (error) {
        console.error('Error fetching service:', error);
      }
    }
    fetchService();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" /></div>;
  if (!service) return <div className="min-h-screen flex items-center justify-center text-emerald-900 font-bold">Service ikke fundet.</div>;

  const plainDescription = service.description?.da
    ? service.description.da.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 155)
    : '';
  const pageTitle = `${service.title?.da || 'Service'} – Effektiv & Professionel | DK Grøn Service`;

  return (
    <div className="pt-20">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={plainDescription || `Professionel ${service.title?.da?.toLowerCase() || 'service'} udført af DK Grøn Service for private og erhverv.`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={plainDescription} />
        <meta property="og:type" content="article" />
      </Helmet>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <Link
            to="/andre-opgaver"
            className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700 transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbage til oversigten
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="space-y-8">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-emerald-950">{service.title.da}</h1>
              <div 
                className="prose prose-lg prose-emerald text-emerald-900/80 leading-relaxed max-w-none"
                dangerouslySetInnerHTML={{ __html: service.description.da }}
              />
              
              <div className="pt-8 border-t border-emerald-100">
                <h3 className="text-xl font-bold text-emerald-950 mb-6">Interesseret i et tilbud?</h3>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:+4542442359"
                    className="flex items-center gap-3 bg-emerald-600 text-white px-6 py-4 rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg"
                  >
                    <Phone className="w-5 h-5" />
                    Ring: 42 44 23 59
                  </a>
                  <a
                    href="mailto:Djensen2@me.com"
                    className="flex items-center gap-3 bg-white text-emerald-900 border border-emerald-100 px-6 py-4 rounded-2xl font-bold hover:bg-emerald-50 transition-all shadow-sm"
                  >
                    <Mail className="w-5 h-5" />
                    Send en email
                  </a>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="aspect-square md:aspect-[4/3] rounded-[3rem] overflow-hidden shadow-2xl">
                <img
                  src={imageUrl(service.image[0], 'width=1000,quality=80,format=auto')}
                  loading="lazy"
                  decoding="async"
                  alt={service.title.da}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="bg-emerald-50 p-10 rounded-[3rem] border border-emerald-100">
                <h3 className="text-2xl font-bold text-emerald-950 mb-6">Hvad du får hos os</h3>
                <ul className="space-y-4">
                  {[
                    'Professionelt udstyr',
                    'Erfaren og stabil service',
                    'Fast pris eller timepris',
                    'Oprydning inkluderet',
                    'Personlig dialog',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-emerald-900 font-medium">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other services slider/grid */}
      <section className="py-24 bg-emerald-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-12">Se også vores andre ydelser</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ejendomsservice"
              className="bg-white px-8 py-4 rounded-2xl font-bold text-emerald-900 border border-emerald-100 hover:bg-emerald-100 transition-all shadow-sm"
            >
              Ejendomsservice
            </Link>
            <Link
              to="/andre-opgaver/graesslaaning"
              className="bg-white px-8 py-4 rounded-2xl font-bold text-emerald-900 border border-emerald-100 hover:bg-emerald-100 transition-all shadow-sm"
            >
              Græsslåning
            </Link>
            <Link
              to="/andre-opgaver/flishugning"
              className="bg-white px-8 py-4 rounded-2xl font-bold text-emerald-900 border border-emerald-100 hover:bg-emerald-100 transition-all shadow-sm"
            >
              Flishugning
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
